"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getAccessPointsQueryKey,
  useFetchAccessPoint,
  useMarketplaceAccessPoints,
} from "../../lib/services/access-point-service";
import { useHistoryManager } from "../history/useHistoryManager";
import {
  setOrganisation,
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
} from "../slice/accessOrg-slice";
import { useAppDispatch } from "../slice/hooks";

export const useMarketplaceOrgs = (initialUrlData = {}, token, limit = 10) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { navigateTo } = useHistoryManager();

  const [state, setState] = useState({
    accessPoints: [],
    limit: limit,
    total: 0,
    currentPage: 1,
    page: 1,
    randomSeed: null,
    search: "",
    ...initialUrlData,
  });

  const [switching, setSwitching] = useState(false);
  const [newAccessPoint, setNewAccessPoint] = useState(null);
  const [accessPointRequest, setAccessPointRequest] = useState(null);

  // --------------------------
  //  FETCH ACCESSPOINTS
  // --------------------------
  const accQueryKey = useMemo(
    () => ["feeds", state.limit, state.page, state.search],
    [state.limit, state.page, state.search],
  );

  const {
    data: feeds,
    isLoading,
    isFetching,
    error,
  } = useMarketplaceAccessPoints({
    queryKey: accQueryKey,
    limit: state.limit,
    page: state.page,
    search: state.search,
    randomSeed: state.randomSeed,
    token,
    onError: (err) => {
      console.error("Failed to fetch accessPoint:", err);
      setSwitching(false);
    },
  });

  useEffect(() => {
    if (feeds && feeds.accessPoints) {
      const { accessPoints, limit, total, currentPage, randomSeed } = feeds;

      setState((prev) => {
        const all = [...(prev.accessPoints || []), ...accessPoints];
        const unique = all.filter(
          (acc, index, self) =>
            index ===
            self.findIndex((a) => a.accessPointId === acc.accessPointId),
        );
        return {
          ...prev,
          accessPoints: unique,
          limit,
          total,
          currentPage,
          randomSeed: prev.page === 1 ? (randomSeed ?? null) : prev.randomSeed,
        };
      });
    }
  }, [feeds]);

  const openPage = (page) => {
    setState((prev) => ({ ...prev, page }));
  };

  const finishAccessPointSwitch = useCallback(
    (response, acc) => {
      const accessPoints = response?.data?.accessPoints || [];
      navigateTo(
        `store/${acc.orgDetails?.handle}`,
        {
          urlData: {
            accessPointId: acc.accessPointId,
            organisation: acc.orgDetails,
            accessPoints: accessPoints,
            accessPoint: acc,
          },
          onNavigate: (navigate) => {
            setSwitching(false);
            if (navigate) {
              if (accessPoints && accessPoints.length > 0) {
                dispatch(setAccessPoints(accessPoints));
              } else {
                console.log("No access points found");
              }
              if (typeof window !== "undefined") {
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }
            }
          },
        },
        false,
      );
    },
    [dispatch, navigateTo],
  );

  // --------------------------
  //  FETCH ACCESS POINT + SWITCH
  // --------------------------
  useFetchAccessPoint({
    orgId: accessPointRequest?.orgId,
    token: accessPointRequest?.token,
    enabled: Boolean(accessPointRequest),
    onError: (error) => {
      setAccessPointRequest(null);
      console.error("Failed to fetch access point:", error);
      setSwitching(false);
    },
    onSuccess: (response) => {
      const request = accessPointRequest;
      setAccessPointRequest(null);
      finishAccessPointSwitch(response, request?.accessPoint || newAccessPoint);
    },
  });

  const handleSwitch = async (acc) => {
    setNewAccessPoint(acc);
    dispatch(setOrganisation(acc.orgDetails));
    dispatch(setAccessPointId(acc.accessPointId));
    dispatch(setAccessPoint(acc));
    setSwitching(true);
    const cachedAccessPointsResponse = queryClient.getQueryData(
      getAccessPointsQueryKey(acc.profileId, token),
    );

    if (cachedAccessPointsResponse) {
      finishAccessPointSwitch(cachedAccessPointsResponse, acc);
      return;
    }

    setAccessPointRequest({
      orgId: acc.profileId,
      token,
      accessPoint: acc,
    });
  };

  return {
    state,
    openPage,
    handleSwitch,
    switching,
    isLoading,
    isFetching,
    error,
  };
};
